import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import Api from '../../../../../constants/Api';
import axiosInstance from '../../../../../services/AxiosInstance';
import FieldValidationError from '../../../Forms/Element/FieldValidationError';
import { createSelectStyles, getSelectMenuPortalTarget, selectThemeProps } from '../../../Forms/Element/selectTheme';

/**
 * Componente responsável por listar os clientes com busca assíncrona.
 *
 * `crossBranch`: ignora o isolamento por filial na busca - uso restrito aos
 * fluxos de agendamento e assinatura do clube, onde o operador precisa
 * localizar um cliente cadastrado em outra filial. Fora desses dois casos,
 * mantenha o padrão (busca só na(s) filial(is) do usuário logado).
 */
const CustomerSelect = (props) => {
  const { t } = useTranslation();
  const { value, onChange, isDisabled, error, styles, crossBranch, ...rest } = props;

  const fetchCustomers = (filters) => {
    const endpoint = crossBranch ? "/pageable/all-branches" : "/pageable";
    return axiosInstance.post(Api.main.customer + endpoint, {
      filters: filters,
      page: 0,
      size: 50,
      orders: [{ field: "firstName", direction: "ASC" }]
    }).then(response => response.data.content || response.data || []);
  };

  const mapToOptions = (data) => {
    const uniqueCustomers = Array.from(
      new Map(data.map(item => [item.id, item])).values()
    );

    return uniqueCustomers.map(item => {
      const customerName = item.fullName || [item.firstName, item.lastName].filter(Boolean).join(' ');
      const customerInfo = item.phone || item.document || '';

      return {
        value: item.id,
        label: `${customerName} (${customerInfo})`,
        customer: item
      };
    });
  };

  const loadOptions = (inputValue, callback) => {
    const filters = [];
    if (inputValue) {
      const cleanInput = inputValue.replace(/[^\d]/g, '');
      const hasLetters = /[a-zA-ZÀ-ÿ]/.test(inputValue);

      if (cleanInput.length > 2 && !hasLetters) {
        Promise.all([
          fetchCustomers([{
            field: "phone",
            value: cleanInput,
            operation: "MATCH"
          }]),
          fetchCustomers([{
            field: "document",
            value: cleanInput,
            operation: "MATCH"
          }])
        ])
          .then(([phoneCustomers, documentCustomers]) => {
            callback(mapToOptions([...phoneCustomers, ...documentCustomers]));
          })
          .catch(err => {
            console.error(err);
            callback([]);
          });
        return;
      }

      filters.push({
        field: "fullName",
        value: inputValue,
        operation: "MATCH"
      });
    }

    fetchCustomers(filters)
      .then(response => {
        callback(mapToOptions(response));
      })
      .catch(err => {
        console.error(err);
        callback([]);
      });
  };

  return (
    <>
      <AsyncSelect
        value={value}
        loadOptions={loadOptions}
        defaultOptions={true}
        onChange={onChange}
        placeholder={t("select")}
        {...selectThemeProps}
        isClearable
        isDisabled={isDisabled}
        menuPortalTarget={getSelectMenuPortalTarget()}
        styles={createSelectStyles(styles)}
        {...rest}
      />
      <FieldValidationError error={error} />
    </>
  );
};

export default CustomerSelect;
